import { APIClientServices } from "./APIClientServices";
import { WSServices } from "./WSServices";
import type { PublicTeamMemberDTO } from "./WorkspaceServices";
import type { StreamCallbacks, SSEToolCall, SSEToolResult, SSEConfirmationRequired, MessageAttachment } from "./AssistantServices";

// Re-export types for convenience
export type { StreamCallbacks } from "./AssistantServices";

// --- DTOs ---

export type TeamSenderType = "user" | "leader" | "member" | "assistente" | "confirmation";

export interface TeamConversationDTO {
	id: string;
	workspaceId: number;
	teamId: number | null;
	userId: string;
	title: string | null;
	isArchived: boolean;
	createdAt: string;
	updatedAt: string;
	messagesCount?: number;
}

export interface TeamMessageDTO {
	id: string;
	conversationId: string;
	senderType: TeamSenderType;
	senderRef: string;
	content: string;
	metadata: Record<string, any>;
	createdAt: string;
}

export interface TeamConversationWithMessagesDTO {
	conversation: TeamConversationDTO;
	messages: TeamMessageDTO[];
	total: number;
}

export interface TeamSendMessageResult {
	userMessage: TeamMessageDTO;
}

export type TaskStatus = "pending" | "processing" | "paused" | "completed" | "failed" | "cancelled";
export type TaskRootStatus = "active" | "completed" | "failed" | "cancelled";
export type SubtaskReviewStatus = "approved" | "rejected" | "needs_changes";
export type SubtaskReviewAction = "approve" | "reject" | "reopen";
export type DeliverableKind = "text" | "mcp_action" | "file" | "link";
export type DeliverableReviewStatus = "pending" | "approved" | "rejected" | "revised";
export type DeliverableReviewAction = "approve" | "reject" | "request_revision";

export interface TaskDeliverableDTO {
	id: string;
	subtaskId: string;
	taskId: string;
	workspaceId: string;
	accountId: string;
	version: number;
	kind: DeliverableKind;
	title: string | null;
	preview: string | null;
	mcpSlug: string | null;
	mcpTool: string | null;
	externalId: string | null;
	externalUrl: string | null;
	data: Record<string, any>;
	createdAt: string;
	reviewStatus: DeliverableReviewStatus;
	reviewNotes: string | null;
	reviewedAt: string | null;
	reviewedBy: string | null;
}

// EK-52 / RF013 — diff entre duas versões de entregáveis.
export interface DiffChange {
	value: string;
	added?: boolean;
	removed?: boolean;
}

export interface FieldDiff {
	field: string;
	baseValue: unknown;
	targetValue: unknown;
	changes: DiffChange[] | null;
	changed: boolean;
}

export interface DeliverableDiffDTO {
	subtaskId: string;
	baseVersion: number;
	targetVersion: number;
	base: TaskDeliverableDTO;
	target: TaskDeliverableDTO;
	changedFields: string[];
	fields: FieldDiff[];
}

export interface TeamTaskSubtaskDTO {
	id: string;
	taskItemId: string;
	teamMemberId: string;
	title: string;
	description: string | null;
	objective: string;
	status: TaskStatus;
	statusDescription: string;
	capacityUsed: number;
	usedMcps: string[];
	executionContext: Record<string, any>;
	interactionCount: number;
	maxInteractions: number;
	lastError: string | null;
	startedAt: string | null;
	completedAt: string | null;
	reviewStatus: SubtaskReviewStatus | null;
	reviewNotes: string | null;
	reviewedAt: string | null;
	reviewedBy: string | null;
	cancelledAt: string | null;
	cancelledBy: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface TeamTaskItemDTO {
	id: string;
	taskId: string;
	title: string;
	description: string | null;
	objective: string;
	subtasks: TeamTaskSubtaskDTO[];
	createdAt: string;
	updatedAt: string;
}

export interface TeamTaskDTO {
	id: string;
	teamId: string;
	workspaceId: string;
	accountId: string;
	objective: string;
	systemPrompt: string;
	status?: TaskRootStatus;
	cancelledAt?: string | null;
	cancelledBy?: string | null;
	cancellationReason?: string | null;
	items: TeamTaskItemDTO[];
	createdAt: string;
	updatedAt: string;
}

export interface WorkspaceTasksPayloadDTO {
	tasks: TeamTaskDTO[];
	members: PublicTeamMemberDTO[];
}

export interface TaskSubtaskRealtimeEvent {
	type: "task_subtask_updated";
	taskId: string;
	taskItemId: string;
	subtaskId: string;
	workspaceId: string;
	teamId: string;
	accountId: string;
	status: TaskStatus;
	statusDescription: string;
	capacityUsed: number;
	usedMcps: string[];
	updatedAt: string;
	completedAt: string | null;
	reviewStatus?: SubtaskReviewStatus | null;
}

export interface TaskSubtaskStatusRealtimeEvent {
	type: "task_subtask_status";
	taskId: string;
	taskItemId: string;
	subtaskId: string;
	workspaceId: string;
	teamId: string;
	accountId: string;
	status: TaskStatus;
	statusDescription: string;
	capacityUsed: number;
	usedMcps: string[];
	updatedAt: string;
	completedAt: string | null;
}

export interface TaskItemRealtimeEvent {
	type: "task_item_updated";
	taskId: string;
	taskItemId: string;
	subtaskId?: string;
	workspaceId: string;
	teamId: string;
	accountId: string;
	updatedAt: string;
}

export interface TaskRealtimeEvent {
	type: "task_updated";
	taskId: string;
	workspaceId: string;
	teamId: string;
	accountId: string;
	objective: string;
	systemPrompt: string;
	updatedAt: string;
}

export interface TaskCreationRealtimeEvent {
	type: "task_created";
	taskId: string;
	workspaceId: string;
	teamId: string;
	accountId: string;
	objective: string;
	systemPrompt: string;
	createdAt: string;
	updatedAt: string;
}

export interface TaskSubtaskToolCallRealtimeEvent {
	toolName: string;
	mcpSlug?: string;
	mcpName?: string;
	taskId?: string;
	taskItemId?: string;
	subtaskId?: string;
	memberId?: string;
	phase: "start" | "end";
	startedAt?: string;
	endedAt?: string;
	durationMs?: number;
	success?: boolean;
	error?: string;
}

export type TaskRealtimeEvents =
	| TaskSubtaskRealtimeEvent
	| TaskSubtaskStatusRealtimeEvent
	| TaskItemRealtimeEvent
	| TaskRealtimeEvent
	| TaskCreationRealtimeEvent;

// --- Service ---

export class TeamLeaderServices {
	private static baseRoute(workspaceId: string, teamId?: string) {
		if (teamId) {
			return `v1/workspaces/${workspaceId}/teams/${teamId}`;
		}
		return `v1/workspaces/${workspaceId}`;
	}

	// ── Conversações ──────────────────────────────────────────────

	/** Cria uma nova conversa no team. */
	static async createConversation(workspaceId: string, title?: string, teamId?: string): Promise<TeamConversationDTO> {
		return APIClientServices.post<TeamConversationDTO>(`${this.baseRoute(workspaceId, teamId)}/conversations`, title ? { title } : undefined);
	}

	/** Lista todas as conversas do usuário no team. */
	static async listConversations(workspaceId: string, teamId?: string): Promise<TeamConversationDTO[]> {
		return APIClientServices.get<TeamConversationDTO[]>(`${this.baseRoute(workspaceId, teamId)}/conversations`);
	}

	/** Obtém uma conversa com histórico de mensagens. */
	static async getConversation(
		workspaceId: string,
		conversationId: string,
		limit = 50,
		offset = 0,
		teamId?: string,
	): Promise<TeamConversationWithMessagesDTO> {
		return APIClientServices.get<TeamConversationWithMessagesDTO>(`${this.baseRoute(workspaceId, teamId)}/conversations/${conversationId}`, {
			limit: limit.toString(),
			offset: offset.toString(),
		});
	}

	// ── Mensagens ─────────────────────────────────────────────────

	/** Envia uma mensagem em background (202 Accepted). */
	static async sendMessage(workspaceId: string, conversationId: string, content: string, teamId?: string, attachments?: MessageAttachment[]): Promise<TeamSendMessageResult> {
		// EK-404 #2: ver AssistantServices.sendMessage
		const idempotencyKey = (typeof crypto !== "undefined" && (crypto as any).randomUUID)
			? (crypto as any).randomUUID()
			: `${Date.now()}-${Math.random().toString(36).slice(2)}`;
		return APIClientServices.post<TeamSendMessageResult>(
			`${this.baseRoute(workspaceId, teamId)}/conversations/${conversationId}/messages`,
			{
				content,
				...(attachments?.length ? { attachments } : {}),
			},
			undefined,
			{ "Idempotency-Key": idempotencyKey },
		);
	}

	// ── Confirmações ──────────────────────────────────────────────

	/** Confirma ou nega a execução de uma ferramenta. */
	static async confirmToolExecution(
		workspaceId: string,
		confirmationId: string,
		approved: boolean,
		reason?: string,
		teamId?: string,
	): Promise<{ success: boolean; confirmationId: string; approved: boolean }> {
		return APIClientServices.post(`${this.baseRoute(workspaceId, teamId)}/confirmations/${confirmationId}`, { approved, reason });
	}

	// ── Processing Status ─────────────────────────────────────────

	/** Obtém o status de processamento da conversa. */
	static async getProcessingStatus(workspaceId: string, conversationId: string, teamId?: string): Promise<any> {
		return APIClientServices.get(`${this.baseRoute(workspaceId, teamId)}/conversations/${conversationId}/processing`);
	}

	// ── Tarefas ───────────────────────────────────────────────────

	/** Lista tarefas do workspace e retorna membros consolidados. */
	static async getTasks(workspaceId: string, status?: TaskStatus): Promise<WorkspaceTasksPayloadDTO> {
		const params: Record<string, string> = {};
		if (status) params.status = status;
		return APIClientServices.get<WorkspaceTasksPayloadDTO>(`${this.baseRoute(workspaceId)}/tasks`, params);
	}

	/** Obtém uma tarefa específica. */
	static async getTask(workspaceId: string, taskId: string): Promise<TeamTaskDTO> {
		return APIClientServices.get<TeamTaskDTO>(`${this.baseRoute(workspaceId)}/tasks/${taskId}`);
	}

	/**
	 * EK-405: Cancela uma task em execução.
	 * Idempotente em estados terminais. Não interrompe LLM em andamento, não
	 * desfaz e-mails/eventos já enviados, não estorna tokens consumidos.
	 */
	static async cancelTask(workspaceId: string, taskId: string, reason?: string): Promise<TeamTaskDTO> {
		return APIClientServices.post<TeamTaskDTO>(
			`${this.baseRoute(workspaceId)}/tasks/${taskId}/cancel`,
			reason ? { reason } : {},
		);
	}

	/**
	 * Revisão humana de uma subtarefa.
	 * - approve: marca como aprovada
	 * - reject: marca como rejeitada (sem novo ciclo)
	 * - reopen: reseta para 'pending', anexa instruções ao objetivo e re-enfileira o ciclo
	 *           (notes >= 3 chars obrigatório).
	 */
	static async reviewSubtask(
		workspaceId: string,
		taskId: string,
		subtaskId: string,
		input: { action: SubtaskReviewAction; notes?: string | null },
	): Promise<TeamTaskSubtaskDTO> {
		return APIClientServices.post<TeamTaskSubtaskDTO>(
			`${this.baseRoute(workspaceId)}/tasks/${taskId}/subtasks/${subtaskId}/review`,
			input,
		);
	}

	/**
	 * Lista o histórico de entregáveis (deliverables) de uma subtarefa,
	 * incluindo todas as versões (reaberturas geram nova versão).
	 */
	static async listSubtaskDeliverables(
		workspaceId: string,
		taskId: string,
		subtaskId: string,
	): Promise<TaskDeliverableDTO[]> {
		return APIClientServices.get<TaskDeliverableDTO[]>(
			`${this.baseRoute(workspaceId)}/tasks/${taskId}/subtasks/${subtaskId}/deliverables`,
		);
	}

	/**
	 * Review humano de UM entregável específico.
	 *
	 * - approve: marca como aprovado
	 * - reject: marca como rejeitado (sem reabrir subtarefa)
	 * - request_revision: marca como 'revised' e anexa notes; o usuário pode
	 *                      em seguida chamar reviewSubtask reopen para gerar
	 *                      nova versão do entregável (workflow desacoplado).
	 *
	 * notes obrigatório (>=3 chars) para reject/request_revision.
	 */
	static async reviewDeliverable(
		workspaceId: string,
		taskId: string,
		deliverableId: string,
		input: { action: DeliverableReviewAction; notes?: string | null },
	): Promise<TaskDeliverableDTO> {
		return APIClientServices.post<TaskDeliverableDTO>(
			`${this.baseRoute(workspaceId)}/tasks/${taskId}/deliverables/${deliverableId}/review`,
			input,
		);
	}

	/**
	 * Cria nova versão (max+1) copiando dados de uma versão antiga.
	 * Não recria recurso externo (não reenvia e-mail, não recria evento).
	 */
	static async rollbackDeliverable(
		workspaceId: string,
		taskId: string,
		deliverableId: string,
	): Promise<TaskDeliverableDTO> {
		return APIClientServices.post<TaskDeliverableDTO>(
			`${this.baseRoute(workspaceId)}/tasks/${taskId}/deliverables/${deliverableId}/rollback`,
			{},
		);
	}

	/**
	 * EK-52 / RF013: diff entre duas versões de entregáveis de um mesmo subtask.
	 * Sem parâmetros, compara a última versão contra a anterior. Texto via jsdiff,
	 * payload `data` via JSON diff, campos escalares com flag `changed`.
	 */
	static async getDeliverableDiff(
		workspaceId: string,
		taskId: string,
		subtaskId: string,
		opts?: { baseVersion?: number; targetVersion?: number },
	): Promise<DeliverableDiffDTO> {
		const qs: string[] = [];
		if (opts?.baseVersion != null) qs.push(`baseVersion=${opts.baseVersion}`);
		if (opts?.targetVersion != null) qs.push(`targetVersion=${opts.targetVersion}`);
		const url = `${this.baseRoute(workspaceId)}/tasks/${taskId}/subtasks/${subtaskId}/deliverables/diff${qs.length ? `?${qs.join("&")}` : ""}`;
		return APIClientServices.get<DeliverableDiffDTO>(url);
	}

	/** Inscreve em atualizações de subtarefas no escopo do workspace. */
	static subscribeToTaskUpdates(
		workspaceId: string,
		callbacks: {
			onSubtaskUpdated?: (event: TaskSubtaskRealtimeEvent) => void;
			onSubtaskStatusUpdated?: (event: TaskSubtaskStatusRealtimeEvent) => void;
			onSubtaskToolCall?: (event: TaskSubtaskToolCallRealtimeEvent) => void;
			onTaskItemUpdated?: (event: TaskItemRealtimeEvent) => void;
			onTaskUpdated?: (event: TaskRealtimeEvent) => void;
			onTaskCreated?: (event: TaskCreationRealtimeEvent) => void;
		},
	): { stop: () => void } {
		const subscriptions = [
			WSServices.onReceive<TaskSubtaskRealtimeEvent>("task:subtask:update", (data) => {
				if (!data || data.type !== "task_subtask_updated") return;
				callbacks.onSubtaskUpdated?.(data);
			}),
			WSServices.onReceive<TaskSubtaskStatusRealtimeEvent>("task:subtask:status", (data) => {
				if (!data || data.type !== "task_subtask_status") return;
				callbacks.onSubtaskStatusUpdated?.(data);
			}),
			WSServices.onReceive<TaskSubtaskToolCallRealtimeEvent>("task:subtask:tool_call", (data) => {
				if (!data || !data.phase) return;
				callbacks.onSubtaskToolCall?.(data);
			}),
			WSServices.onReceive<TaskItemRealtimeEvent>("task:item:update", (data) => {
				if (!data || data.type !== "task_item_updated") return;
				callbacks.onTaskItemUpdated?.(data);
			}),
			WSServices.onReceive<TaskRealtimeEvent>("task:update", (data) => {
				if (!data || data.type !== "task_updated") return;
				callbacks.onTaskUpdated?.(data);
			}),
			WSServices.onReceive<TaskCreationRealtimeEvent>("task:creation", (data) => {
				if (!data || data.type !== "task_created") return;
				callbacks.onTaskCreated?.(data);
			}),
			// Compatibilidade com contratos antigos que enviavam um canal agregado por workspace.
			WSServices.onReceive<TaskSubtaskRealtimeEvent>(`workspace:${workspaceId}:tasks`, (data) => {
				if (!data || data.type !== "task_subtask_updated") return;
				callbacks.onSubtaskUpdated?.(data);
			}),
		];

		return {
			stop: () => subscriptions.forEach((subscription) => subscription.stop()),
		};
	}

	// ── WebSocket ─────────────────────────────────────────────────

	/**
	 * Gera o nome do evento WebSocket para streaming de uma conversa de team.
	 * Formato: `team:{teamId}:conversation:{conversationId}:stream`
	 */
	static getStreamEventName(teamId: string | number, conversationId: string): string {
		return `team:${teamId}:conversation:${conversationId}:stream`;
	}

	/**
	 * Inscreve-se nos eventos WebSocket de uma conversa de team.
	 * @param teamId - ID numérico do team
	 * @param conversationId - ID da conversa
	 * @param callbacks - Callbacks para cada tipo de evento
	 * @returns Objeto com método `stop()` para cancelar a assinatura
	 */
	static subscribeToConversation(teamId: string | number, conversationId: string, callbacks: StreamCallbacks): { stop: () => void } {
		const eventName = this.getStreamEventName(teamId, conversationId);

		const handler = (data: any) => {
			if (!data || !data.type) return;

			switch (data.type) {
				case "message_started":
					callbacks.onProcessingStarted?.(data);
					break;
				case "token":
					callbacks.onToken?.(data);
					break;
				case "tool_call":
					callbacks.onToolCall?.({
						type: "tool_call",
						name: data.toolCall?.name ?? data.name,
						id: data.toolCall?.id ?? data.id,
						arguments: data.toolCall?.arguments ?? data.arguments,
					} as SSEToolCall);
					break;
				case "tool_result":
					callbacks.onToolResult?.({
						type: "tool_result",
						name: data.functionName ?? data.name,
						id: data.toolCallId ?? data.id,
						result: data.result,
					} as SSEToolResult);
					break;
				case "confirmation_required":
					callbacks.onConfirmationRequired?.({
						type: "confirmation_required",
						confirmationId: data.confirmation?.confirmationId ?? data.confirmationId,
						toolName:
							data.confirmation?.toolName ?? data.toolName ?? data.confirmation?.functionName ?? data.functionName ?? "unknown_tool",
						description: data.confirmation?.description ?? data.description,
						arguments: data.confirmation?.arguments ?? data.arguments,
						toolCallId: data.confirmation?.toolCallId ?? data.toolCallId,
						userId: data.confirmation?.userId ?? data.userId,
					} as SSEConfirmationRequired);
					break;
				case "confirmation_resolved":
					callbacks.onConfirmationResolved?.(data);
					break;
				case "confirmation_message":
					callbacks.onConfirmationMessage?.(data);
					break;
				case "complete":
					callbacks.onComplete?.(data);
					break;
				case "message_saved":
					callbacks.onMessageSaved?.(data);
					break;
				case "message_status":
					callbacks.onProcessingStatus?.(data);
					break;
				case "message_completed":
					callbacks.onProcessingCompleted?.(data);
					callbacks.onDone?.({ type: "done" });
					break;
				case "message_error":
					callbacks.onProcessingError?.(data);
					callbacks.onError?.({ type: "error", message: data.error || "Erro no processamento" });
					break;
				case "error":
					callbacks.onError?.(data);
					break;
			}
		};

		const subscription = WSServices.onReceive(eventName, handler);

		return {
			stop: () => {
				subscription.stop();
			},
		};
	}
}
